import React, { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { connect, useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { compose, Dispatch } from 'redux'

import OrganizationAdminView from './OrganizationAdminView'

import Text from 'admin/components/common/Text'
import PersonForm, { PersonFormData } from 'admin/components/pages/PersonForm'
import { State } from 'admin/redux/ducks'
import { fetchOrgAccount } from 'admin/redux/ducks/orgAccount'
import {
  updateOrgAdmin,
  createOrgAdmin,
  fetchOrganizationAdmin,
  inactivateAdministrator
} from 'admin/redux/ducks/organizationAdmin'
import { withScrollToTop } from 'components/common/withScrollToTop'
import { ModalContainer, ModalHeader, ModalFooter } from 'components/ui/Modal'
import { hideModalAction, showModalAction } from 'redux/ducks/modal'
import { showToastAction } from 'redux/ducks/toast'
import { AlertLevels, ErrorType, PersonType } from 'types'

const initialFormValues: PersonFormData = {
  personalId: '',
  firstName: '',
  lastName: '',
  email: '',
  telephoneNumber: '',
}

const OrganizationAdmin: React.FC<OrganizationAdminProps> = ({ editing }) => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const dispatch = useDispatch()

  const { login, orgAdmin, orgAccount } = useSelector((state: State) => ({
    login: state.login,
    orgAdmin: state.organizationAdmin,
    orgAccount: state.orgAccount,
  }))

  let formValues = initialFormValues
  if (editing && id && orgAdmin.data) {
    formValues = {
      kimId: orgAdmin.data.kimId,
      personalId: orgAdmin.data.personalId,
      firstName: orgAdmin.data.firstName,
      lastName: orgAdmin.data.lastName,
      email: orgAdmin.data.email,
      telephoneNumber: orgAdmin.data.telephoneNumber,
    }
  }

  const formMethods = useForm<PersonFormData>({
    defaultValues: formValues,
    mode: 'onBlur',
  })

  const onSubmit = (values: PersonFormData) => {
    const parsedId = parseInt(id, 10)
    
    // Edit existing admin
    if (parsedId && !Number.isNaN(parsedId)) {
      const requestData = values
      const { orgAccountId } =
        orgAccount.data.administrators.find(
          (admin) => admin.kimId === values.kimId
        ) || orgAccount.data
      return dispatch(
        updateOrgAdmin(orgAccountId, parsedId, requestData as PersonType)
      ).then(() => {
        navigate(`/organisation/admin/visa/${parsedId}`)
        dispatch(
          showToastAction({
            level: AlertLevels.SUCCESS,
            message: 'organization_admin_edited',
          })
        )
      })
    }
    
    // Create new admin - exclude kimId from requestData
    const { kimId: _kimId, ...requestData } = values
    const { orgAccountId } = orgAccount.data
    return dispatch(createOrgAdmin(orgAccountId, requestData))
      .then((_kimId) => {
        dispatch(
          showToastAction({
            level: AlertLevels.SUCCESS,
            message: 'organization_admin_created',
          })
        )
        navigate('/organisation')
        // TODO: Use and return kimId when API returns it
        // this.props.history.push('/organisation/admin/visa/' + kimId)
        // return kimId
      })
      .catch((_error) => {
        // Potential errors are handled earlier. Empty catch here to avoid uncaught exceptions
      })
  }
  const deleteAdmin = () => {
    dispatch(
      inactivateAdministrator(login.data.orgNumber, orgAdmin.data.kimId)
    ).then(() => {
      navigate('/organisation')
      dispatch(
        showToastAction({
          level: AlertLevels.SUCCESS,
          message: 'organization_admin_deleted',
        })
      )
    })
  }
  const showInactivateConfirmation = () => {
    dispatch(
      showModalAction(
        <DeleteOrgAdminConfirmationModal
          onInactivate={() => {
            dispatch(hideModalAction())
            deleteAdmin()
          }}
        />,
        {
          closable: true,
        }
      )
    )
  }

  useEffect(() => {
    const { orgAccountId } = login.data
    if (id) {
      dispatch(fetchOrganizationAdmin(orgAccountId, parseInt(id, 10))).then(
        () => {
          if (!orgAccount.data) {
            dispatch(fetchOrgAccount(orgAccountId))
          }
        }
      )
    } else if (!orgAccount.data) {
      dispatch(fetchOrgAccount(orgAccountId))
    }
  }, [dispatch, id, login.data, orgAccount.data])

  useEffect(() => {
    if (editing && id && orgAdmin.data) {
      const newFormValues = {
        kimId: orgAdmin.data.kimId,
        personalId: orgAdmin.data.personalId,
        firstName: orgAdmin.data.firstName,
        lastName: orgAdmin.data.lastName,
        email: orgAdmin.data.email,
        telephoneNumber: orgAdmin.data.telephoneNumber,
      }
      formMethods.reset(newFormValues)
    }
  }, [editing, id, orgAdmin.data, formMethods])

  const newAdmin = !(id && orgAdmin.data)

  if (editing) {
    return (
      <FormProvider {...formMethods}>
        <PersonForm
          newPerson={newAdmin}
          errors={orgAdmin.error}
          pageTitle={
            id ? (
              <h1>
                <Text>general.edit_organization_administrator</Text>
              </h1>
            ) : (
              <h1>
                <Text>general.create_new_organization_administrator</Text>
              </h1>
            )
          }
          backLink={
            <Link to="/organisation/" className="button button--hollow">
              <Text>general.back</Text>
            </Link>
          }
          submitButtonText={
            id ? (
              <span>
                <Text>general.edit_organization_administrator</Text>
              </span>
            ) : (
              <span>
                <Text>general.create_new_organization_administrator</Text>
              </span>
            )
          }
          onSubmit={onSubmit}
        />
      </FormProvider>
    )
  }
  return (
    <OrganizationAdminView
      login={login}
      orgAdmin={orgAdmin}
      deleteCallback={showInactivateConfirmation}
    />
  )
}

type OrganizationAdminProps = {
  editing?: boolean
}

type DispatchProps = {
  fetchOrgAccount: (orgAccountId: string) => Promise<any>
  fetchOrganizationAdmin: (orgAccountId: string, kimId: number) => Promise<any>
  createOrgAdmin: (orgAccountId: string, person: any) => Promise<any>
  updateOrgAdmin: (
    orgAccountId: string,
    kimId: number,
    requestData: any
  ) => Promise<any>
  inactivateAdministrator: (orgNumber: string, kimId: number) => Promise<any>
  showModal: (component: React.ReactNode, options?: any) => void
  hideModal: () => void
  showToast: (alert: ErrorType, options?: any) => void
}
const mapDispatchToProps = (dispatch: Dispatch<any,State>): DispatchProps => {
  return {
    fetchOrgAccount: (orgAccountId) => dispatch(fetchOrgAccount(orgAccountId)),
    fetchOrganizationAdmin: (orgAccountId, kimId) =>
      dispatch(fetchOrganizationAdmin(orgAccountId, kimId)),
    updateOrgAdmin: (orgAccountId, kimId, requestData) =>
      dispatch(updateOrgAdmin(orgAccountId, kimId, requestData)),
    createOrgAdmin: (orgAccountId, person) =>
      dispatch(createOrgAdmin(orgAccountId, person)),
    inactivateAdministrator: (orgNumber, kimId) =>
      dispatch(inactivateAdministrator(orgNumber, kimId)),
    showToast: (alert, options?) => dispatch(showToastAction(alert, options)),
    showModal: (component, options?) =>
      dispatch(showModalAction(component, options)),
    hideModal: () => dispatch(hideModalAction()),
  }
}
export default compose<React.FC<OrganizationAdminProps>>(
  connect<{}, DispatchProps, OrganizationAdminProps>(null, mapDispatchToProps),
  withScrollToTop
)(OrganizationAdmin,
  {
    onMount: true,
    onUpdate:true
  }
)

const DeleteOrgAdminConfirmationModal: React.FunctionComponent<any> = (
  props
) => {
  return (
    <ModalContainer>
      <ModalHeader text={<Text>label.delete_admin_confirmation</Text>} />
      <ModalFooter>
        <button onClick={props.onInactivate}>
          <Text>general.delete</Text>
        </button>
        <div className="l-spacer-inline" />
        <button className="button--hollow" onClick={props.hideModal}>
          <Text>general.cancel</Text>
        </button>
      </ModalFooter>
    </ModalContainer>
  )
}
